import { Box, TableCell, TableSortLabel } from '@mui/material';
import { useLatest } from '@qfa/react-components';
import clsx from 'clsx';
import { SortOrder } from '@kinetic-ui/data-layer';
import { useTranslation } from 'react-i18next';

import { useSort } from '../hooks';
import { useTableStyles } from '../styles';
import { TableHeaderProps } from './types';
import { useEffect, useRef } from 'react';

const TableHeader: React.FunctionComponent<TableHeaderProps> = (props) => {
  const { onSortChange, orderedColumns, hasSubRow, sort, tableHeaderHeight, setTableHeaderHeight } =
    props;
  const { t } = useTranslation('common');
  const classes = useTableStyles();
  const [sortEnabled, sortOrder, onSortClick] = useSort(onSortChange, sort);
  const latestSortClick = useLatest(onSortClick);
  const headerRef = useRef<HTMLDivElement | null>();

  useEffect(() => {
    if (headerRef?.current) {
      const newTableHeaderHeight = headerRef.current.offsetHeight;

      if (tableHeaderHeight !== newTableHeaderHeight) {
        setTableHeaderHeight?.(headerRef.current.offsetHeight);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headerRef.current, setTableHeaderHeight]);

  return (
    <>
      {hasSubRow && <TableCell className={classes.actionCell} key='subRowExpand' />}
      {orderedColumns.map(
        ({ headClassName, className, title, name, noSort, field, minWidth, titleNode }) => {
          const columnIsSortable = sortEnabled && !noSort;
          const renderedTitle = titleNode ?? title;
          const sortField = field ? field : name;
          const currentSortValue = sortOrder?.[sortField];
          return (
            <TableCell
              className={clsx(headClassName, className, classes.header, {
                [classes.sortableHeader]: columnIsSortable,
                [classes.minWidthCell]: minWidth,
              })}
              style={{ minWidth: minWidth && typeof minWidth === 'number' ? minWidth : '' }}
              onClick={
                columnIsSortable
                  ? () => {
                      latestSortClick.current(sortField);
                    }
                  : undefined
              }
              key={`head_${name}`}
              ref={headerRef}
            >
              <Box display={'flex'}>
                {columnIsSortable ? (
                  <Box>
                    <TableSortLabel
                      active={!!currentSortValue}
                      classes={{
                        icon: classes.sortIcon,
                      }}
                      hideSortIcon={false}
                      style={{ color: 'inherit' }}
                      direction={currentSortValue === SortOrder.Descending ? 'desc' : 'asc'}
                      aria-label={t('sortColumn', { column: name })}
                    >
                      {renderedTitle}
                    </TableSortLabel>
                  </Box>
                ) : (
                  renderedTitle
                )}
              </Box>
            </TableCell>
          );
        }
      )}
    </>
  );
};

export default TableHeader;
